import { QueryClient, QueryFilters } from "@tanstack/react-query";
import { ParamsForMethod, QueryDataForMethod, QueryMethodForModel, ModelName, TellescopeQueryKey } from "../../types";
import { TellescopeQueryFilters } from "../../hooks";
import { removeTellescopeQueries } from "./remove-tellescope-queries";

/**
 * Arguments for removing all queries for a specific Tellescope model.
 *
 * @template Model - The Tellescope model name (must support query methods)
 * @template TError - The error type for the queries (defaults to Error)
 */
export interface RemoveTellescopeModelQueriesArgs<Model extends ModelName, TError = Error> {
  /** The Tellescope model to remove queries for */
  model: Model;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
  /**
   * Optional filters applied to all queries for this model.
   * These filters are combined with method-specific filters if both are provided.
   */
  filters?: Omit<
    TellescopeQueryFilters<
      QueryDataForMethod<Model, QueryMethodForModel<Model>>,
      TError,
      QueryDataForMethod<Model, QueryMethodForModel<Model>>,
      TellescopeQueryKey<Model, QueryMethodForModel<Model>>
    >,
    "queryKey"
  >;
  /**
   * Optional method-specific filters. Allows removing queries for specific methods
   * and optionally matching by request parameters.
   */
  methodFilters?: Partial<{
    [Method in QueryMethodForModel<Model>]: Omit<
      TellescopeQueryFilters<
        QueryDataForMethod<Model, Method>,
        TError,
        QueryDataForMethod<Model, Method>,
        TellescopeQueryKey<Model, Method>
      >,
      "queryKey"
    > & {
      /** Optional request parameters to match for this method */
      request?: Partial<ParamsForMethod<Model, Method>>;
    };
  }>;
}

/**
 * Removes all cached queries for a specific Tellescope model.
 *
 * This function provides a way to remove all queries for a model, optionally
 * with method-specific filters. When no methodFilters are provided, it removes
 * all queries for the model. When methodFilters are provided, it only removes
 * queries for the specified methods.
 *
 * @template Model - The Tellescope model name (must support query methods)
 * @template TError - The error type for the queries (defaults to Error)
 *
 * @param args - Configuration object containing model, queryClient, and optional filters
 * @returns void
 *
 * @example
 * ```ts
 * // Remove all user queries
 * removeTellescopeModelQueries({
 *   queryClient,
 *   model: "users",
 * });
 * ```
 *
 * @example
 * ```ts
 * // Remove only specific methods
 * removeTellescopeModelQueries({
 *   queryClient,
 *   model: "users",
 *   methodFilters: {
 *     getOne: {
 *       // Remove all getOne queries
 *     },
 *     getSome: {
 *       request: { filter: { fname: "John" }, limit: 10 },
 *     },
 *   },
 * });
 * ```
 *
 * @example
 * ```ts
 * // Use global filters with method-specific filters
 * removeTellescopeModelQueries({
 *   queryClient,
 *   model: "users",
 *   filters: {
 *     stale: true, // Only remove stale queries
 *   },
 *   methodFilters: {
 *     getOne: {
 *       // Inherits stale: true from global filters
 *     },
 *   },
 * });
 * ```
 */
export const removeTellescopeModelQueries = <Model extends ModelName, TError = Error>({
  model,
  queryClient,
  filters,
  methodFilters,
}: RemoveTellescopeModelQueriesArgs<Model, TError>) => {
  const methodNames = Object.keys(methodFilters ?? {}) as QueryMethodForModel<Model>[];

  // If no method filters are specified, remove all queries for the model
  if (!methodFilters || !methodNames.length) {
    return queryClient.removeQueries({
      queryKey: [model],
      exact: false,
      ...filters,
    } as QueryFilters<Model[]>);
  }

  // Otherwise, remove queries for each specified method
  methodNames.forEach(<Method extends QueryMethodForModel<Model>>(method: Method) => {
    const { request, ...methodSpecificFilters } = methodFilters[method] ?? {};

    removeTellescopeQueries<Model, Method, TError>({
      model,
      method,
      request,
      queryClient,
      filters: {
        ...filters,
        ...methodSpecificFilters,
      },
    });
  });
};
